import { getLastTouched, saveLastTouched } from "../Logic/LastTouched/misc";
import Statuses from "../interfaces/Statuses";
import { iCommunication, iEnquiries, iEnquiry } from "../interfaces/iEnquiry";

export type SortEnquiriesParams = {
    orderBy: keyof iEnquiry | null | string;
    desc?: boolean;
};

export function sortUnresolved(enquiries: iEnquiry[]) {
    return enquiries.sort(
        // first being new > priority > expiry
        (a: iEnquiry, b: iEnquiry) => {
            if (!a.hasNew && b.hasNew) {
                return 1;
            } else if (a.hasNew && !b.hasNew) {
                return -1;
            }

            if (a.priority > b.priority) {
                return 1;
            } else if (a.priority < b.priority) {
                return -1;
            }

            let aTimeout = a?.activeSlaCycle?.timeout ? new Date(a?.activeSlaCycle?.timeout).getTime() : undefined;
            let bTimeout = b?.activeSlaCycle?.timeout ? new Date(b?.activeSlaCycle?.timeout).getTime() : undefined;

            if (!aTimeout && !bTimeout) {
                return 0;
            } else if (!aTimeout || aTimeout > bTimeout!) {
                return 1;
            } else if (!bTimeout || aTimeout < bTimeout!) {
                return -1;
            }

            return 0;
        },
    );
}

export function sortResolved(enquiries: iEnquiry[]) {
    return enquiries.sort(
        // first being latest completed
        (a: iEnquiry, b: iEnquiry) => {
            return new Date(b.dateCompleted) > new Date(a.dateCompleted)
                ? 1
                : new Date(b.dateCompleted) < new Date(a.dateCompleted)
                  ? -1
                  : 0;
        },
    );
}

export function sortStatus(enquiries: iEnquiry[], desc: boolean = false) {
    return enquiries.slice().sort(
        // first being latest completed
        (a: iEnquiry, b: iEnquiry) => {
            const statusA = Statuses.Name(a.status).toLocaleLowerCase();
            const statusB = Statuses.Name(b.status).toLocaleLowerCase();
            return desc
                ? statusB > statusA
                    ? 1
                    : statusB < statusA
                      ? -1
                      : 0
                : statusB < statusA
                  ? 1
                  : statusB > statusA
                    ? -1
                    : 0;
        },
    );
}

export function sortOwner(enquiries: iEnquiry[], desc: boolean = false) {
    return enquiries.slice().sort(
        // first being latest completed
        (a: iEnquiry, b: iEnquiry) => {
            const ownerA = a.owners?.length
                ? a.owners?.[0].lastname
                    ? a.owners?.[0].lastname.toLocaleLowerCase()
                    : a.owners?.[0].firstname
                      ? a.owners?.[0].firstname.toLocaleLowerCase()
                      : ""
                : "";
            const ownerB = b.owners?.length
                ? b.owners?.[0].lastname
                    ? b.owners?.[0].lastname.toLocaleLowerCase()
                    : b.owners?.[0].firstname
                      ? b.owners?.[0].firstname.toLocaleLowerCase()
                      : ""
                : "";
            return desc
                ? ownerB > ownerA
                    ? 1
                    : ownerB < ownerA
                      ? -1
                      : 0
                : ownerB < ownerA
                  ? 1
                  : ownerB > ownerA
                    ? -1
                    : 0;
        },
    );
}

export function sortQueues(enquiries: iEnquiry[]) {
    return enquiries.sort(
        // first being latest received
        (a: iEnquiry, b: iEnquiry) => {
            return new Date(b.dateReceived) > new Date(a.dateReceived)
                ? 1
                : new Date(b.dateReceived) < new Date(a.dateReceived)
                  ? -1
                  : 0;
        },
    );
}

export function sortEnquiriesByField(enquiries: iEnquiry[], fieldName: keyof iEnquiry | string, desc?: boolean) {
    switch (fieldName) {
        case "owner":
            return sortOwner(enquiries, desc);
        case "status":
            return sortStatus(enquiries, desc);
        default:
            return sortByField(enquiries, fieldName, desc);
    }
}

/**
 * Sorts an array of elements by field name.
 * This method copies the array and returns a reference to the copy array.
 * The elements are sorted in ascending, ASCII character order.
 * @param elements List of elements with the same type.
 * @param fieldName The key of the elements in list
 * @param desc Sort by descending order if true, ascending if false or not specified.
 * ```ts
 * interface Person {
 *  name: string,
 *  age: number
 * }
 * const people: Person[] = [
 *  { name: "Bob", age: 41 },
 *  { name: "Alice", age: 30 },
 *  { name: "Charlie", age: 25 },
 * ]
 * const sortedPeopleByName = sortByField(people, "name")
 * // or
 * const sortedPeopleByAgeDesc = sortByField(people, "age", true)
 * ```
 */
export function sortByField<T>(elements: T[], fieldName: keyof T | string, desc: boolean = false): T[] {
    return elements.slice().sort((a, b) => {
        let fieldA = typeof fieldName === "string" ? getInnerFieldValue(a, fieldName) : a[fieldName];
        let fieldB = typeof fieldName === "string" ? getInnerFieldValue(b, fieldName) : b[fieldName];
        fieldA = typeof fieldA === "string" ? fieldA.toLocaleLowerCase() : fieldA;
        fieldB = typeof fieldB === "string" ? fieldB.toLocaleLowerCase() : fieldB;

        const isEmptyA = fieldA === null || fieldA === undefined || fieldA === "";
        const isEmptyB = fieldB === null || fieldB === undefined || fieldB === "";

        if (isEmptyA && isEmptyB) return 0;
        if (isEmptyA) return 1;
        if (isEmptyB) return -1;
        return desc ? (fieldB > fieldA ? 1 : fieldB < fieldA ? -1 : 0) : fieldB < fieldA ? 1 : fieldB > fieldA ? -1 : 0;
    });
}

export function getInnerFieldValue(obj: any, fieldName: string): any {
    const objKeys = fieldName.split(".");
    let result = obj;

    for (const key of objKeys) {
        if (result === undefined) {
            return undefined;
        }

        if (key.includes("[")) {
            // indexed field
            const [arrayField, index] = key.split("[");
            result = result[arrayField][parseInt(index)];
        } else {
            result = result[key];
        }
    }

    return result;
}

/**
 * Resets sort to a currently active list
 */
export const resetSort = () => {
    const LastTouched = getLastTouched();
    if (LastTouched.lastActiveListType == "Unresolved" || LastTouched.lastActiveListType == "Resolved") {
        const activeList = LastTouched.MyEnquiries.ActiveList;
        LastTouched.MyEnquiries.EnquiryLists.find(l => l.Name == activeList)!.orderBy = undefined;
    } else if (LastTouched.lastActiveListType === "Results") {
        LastTouched.SearchResults.EnquiryLists[0].orderBy = undefined;
    } else if (!isNaN(+LastTouched.lastActiveListType)) {
        const activeList = LastTouched.Queue.ActiveList;
        if (activeList != 0) LastTouched.Queue.EnquiryLists.find(l => l.Name == activeList)!.orderBy = undefined;
    }
    saveLastTouched(LastTouched);
};
